import React, { useState, useEffect } from 'react';

class ImageOptimizerService {
  /**
   * Optimize an image URL by adding size and quality parameters.
   */
  optimizeImageUrl(url, width = 300, quality = 80) {
    if (!url) return '';
    if (url.includes('w=') || url.includes('width=')) {
      return url;
    }

    if (url.includes('cloudinary.com')) {
      return url.replace('/upload/', `/upload/w_${width},q_${quality}/`);
    }

    if (url.includes('imgix.net')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}w=${width}&q=${quality}`;
    }

    return url;
  }

  /**
   * Compress and optimize an image file for uploading.
   */
  async optimizeImageForUpload(file, options = {}) {
    const { maxWidth = 800, maxHeight = 600, quality = 0.8, maxFileSize = 500 * 1024 } = options;

    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('File must be an image'));
        return;
      }

      if (file.size <= maxFileSize && file.type === 'image/webp') {
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to process image'));
                return;
              }

              const optimizedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
                type: 'image/webp',
                lastModified: Date.now(),
              });

              resolve(optimizedFile);
            },
            'image/webp',
            quality,
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Create thumb, medium, and large variants of an image.
   */
  async createImageVariants(file) {
    const sizes = [
      { width: 300, height: 200, suffix: 'thumb' },
      { width: 600, height: 400, suffix: 'medium' },
      { width: 800, height: 600, suffix: 'large' },
    ];

    const variants = {};

    for (const size of sizes) {
      try {
        const optimized = await this.optimizeImageForUpload(file, {
          maxWidth: size.width,
          maxHeight: size.height,
          quality: 0.8,
        });
        variants[size.suffix] = optimized;
      } catch (error) {
        console.warn(`[imageOptimization] Failed to generate variant ${size.suffix}:`, error);
      }
    }

    return variants;
  }

  /**
   * Validate file size and type boundaries.
   */
  validateImageFile(file) {
    const errors = [];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      errors.push('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push('Image size must be less than 5MB');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get fallback base64 or placeholder URLs based on item category.
   */
  getFallbackImage(category, itemName = '') {
    const IMG = (id) => `https://images.unsplash.com/photo-${id}?w=800&auto=format&fit=crop&q=80`;

    const fallbackImages = {
      Snacks: IMG('1565557623262-b51c2513a641'),
      'Main Course': IMG('1585937421612-70a008356fbe'),
      Tacos: IMG('1563379091339-03246963d96a'),
      Burritos: IMG('1631452180519-c014fe946bc7'),
      Entradas: IMG('1601050690597-df0568f70950'),
      'Platos Fuertes': IMG('1603894584373-5ac82b2ae398'),
      Bebidas: IMG('1571934811356-5cc061b6821f'),
      Postres: IMG('1586985289902-1cba4c8b3f0b'),
      Beverages: IMG('1571934811356-5cc061b6821f'),
      Dessert: IMG('1586985289902-1cba4c8b3f0b'),
      Offers: IMG('1585937421612-70a008356fbe'),
      default: IMG('1585937421612-70a008356fbe'),
    };

    return fallbackImages[category] || fallbackImages.default;
  }

  /**
   * Process food items array ensuring they all contain valid image targets.
   */
  processFoodItems(items) {
    return items.map((item) => {
      const isInvalid =
        !item.image ||
        (typeof item.image === 'string' &&
          (item.image.startsWith('blob:') ||
            item.image.startsWith('data:image/svg') ||
            item.image.includes('images.pexels.com/photos/247466') ||
            item.image.includes('images.pexels.com/photos/5560763') ||
            item.image.includes('images.pexels.com/photos/674574') ||
            item.image.includes('images.pexels.com/photos/2092500')));
      return {
        ...item,
        image: isInvalid ? this.getFallbackImage(item.category, item.name) : item.image,
        hasValidImage: !isInvalid,
      };
    });
  }

  /**
   * Preload critical images into browser cache.
   */
  preloadImages(imageUrls) {
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }

  /**
   * Resize and compress a user avatar image to Base64 format.
   */
  async optimizeProfileImage(file, { maxSize = 256, quality = 0.7 } = {}) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new window.Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let dataUrl;
          if (file.type === 'image/png' && this._hasAlpha(img)) {
            dataUrl = canvas.toDataURL('image/png', quality);
          } else {
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Helper: check if image has transparency.
   * @private
   */
  _hasAlpha(img) {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, img.width, img.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
    return false;
  }
}

// Instantiate the singleton service
export const imageOptimizer = new ImageOptimizerService();

// Backwards-compatible bindings
export const optimizeImageUrl = imageOptimizer.optimizeImageUrl.bind(imageOptimizer);
export const optimizeImageForUpload = imageOptimizer.optimizeImageForUpload.bind(imageOptimizer);
export const createImageVariants = imageOptimizer.createImageVariants.bind(imageOptimizer);
export const validateImageFile = imageOptimizer.validateImageFile.bind(imageOptimizer);
export const getFallbackImage = imageOptimizer.getFallbackImage.bind(imageOptimizer);
export const processFoodItems = imageOptimizer.processFoodItems.bind(imageOptimizer);
export const preloadImages = imageOptimizer.preloadImages.bind(imageOptimizer);
export const optimizeProfileImage = imageOptimizer.optimizeProfileImage.bind(imageOptimizer);

// React components remain exported as standard functions
export const FoodImageWithFallback = ({
  src,
  alt,
  className,
  category = null,
  itemName = '',
  onError = null,
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      const fallback = getFallbackImage(category, itemName);
      setCurrentSrc(fallback);
      if (onError) onError(fallback);
    }
  };

  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={handleError}
      {...props}
    />
  );
};

export const LazyImage = ({
  src,
  alt,
  className,
  placeholder = null,
  fallbackImage = null,
  category = null,
  onError = null,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder || getFallbackImage(category));
  const [imageRef, setImageRef] = useState();
  const [hasError, setHasError] = useState(false);

  const getFallback = () => {
    if (fallbackImage) return fallbackImage;
    if (category) return getFallbackImage(category);
    return getFallbackImage('default');
  };

  const handleImageError = () => {
    if (!hasError) {
      setHasError(true);
      const fallback = getFallback();
      setImageSrc(fallback);
      if (onError) onError(fallback);
    }
  };

  useEffect(() => {
    let observer;
    let didCancel = false;

    if (imageRef && imageSrc === (placeholder || getFallbackImage(category)) && src) {
      if (window.IntersectionObserver) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!didCancel && (entry.intersectionRatio > 0 || entry.isIntersecting)) {
                setImageSrc(src);
                observer.unobserve(imageRef);
              }
            });
          },
          {
            threshold: 0.01,
            rootMargin: '75%',
          },
        );
        observer.observe(imageRef);
      } else {
        setImageSrc(src);
      }
    }
    return () => {
      didCancel = true;
      if (observer && observer.unobserve) {
        observer.unobserve(imageRef);
      }
    };
  }, [src, imageSrc, imageRef, category, placeholder]);

  return (
    <img
      ref={setImageRef}
      src={imageSrc}
      alt={alt}
      className={className}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={handleImageError}
      {...props}
    />
  );
};

export const ProgressiveImage = ({
  src,
  alt,
  className,
  category = null,
  placeholderColor = '#f3f4f6',
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setHasError] = useState(false);

  useEffect(() => {
    if (!src) {
      const fallback = getFallbackImage(category);
      setCurrentSrc(fallback);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    img.onload = () => {
      setCurrentSrc(src);
      setIsLoading(false);
    };
    img.onerror = () => {
      setHasError(true);
      const fallback = getFallbackImage(category);
      setCurrentSrc(fallback);
      setIsLoading(false);
    };
    img.src = src;
  }, [src, category]);

  return (
    <div className={`${className} relative overflow-hidden`}>
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center animate-pulse"
          style={{ backgroundColor: placeholderColor }}
        >
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
        </div>
      )}

      <img
        src={currentSrc}
        alt={alt}
        className={`${className} transition-opacity duration-300`}
        style={{
          opacity: isLoading ? 0 : 1,
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
        loading="lazy"
        {...props}
      />
    </div>
  );
};

export const useImageUpload = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const processAndUpload = async (file, uploadFunction, options = {}) => {
    setIsProcessing(true);
    setProgress(0);

    try {
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      setProgress(20);

      const optimizedFile = await optimizeImageForUpload(file, options);
      setProgress(60);

      const result = await uploadFunction(optimizedFile);
      setProgress(100);

      return result;
    } catch (error) {
      throw error;
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  return {
    processAndUpload,
    isProcessing,
    progress,
  };
};
